import React, { useState, useEffect, useCallback } from 'react';
import { adminService } from '../services/api';
import { ADMIN_OPS_REFRESH } from '../constants/adminEvents';
import { PACKAGE_TYPE_DIMENSIONS_CM } from '../constants/storeCategories';
import './PackagingManagement.css';

const packageLabel = (code) => {
  const k = typeof code === 'string' ? code.trim() : '';
  return PACKAGE_TYPE_DIMENSIONS_CM[k]?.label || (k === '' ? '— (revisar en productos)' : k);
};

const fulfillmentShort = (f) => {
  if (f === 'supplier') return 'Proveedor';
  if (f === 'javiland') return 'Javiland';
  return f || '—';
};

const statusBadgeClass = (status) =>
  status === 'ready_to_ship' ? 'badge-ready_to_ship' : 'badge-awaiting_packaging';

const statusBadgeText = (status) =>
  status === 'ready_to_ship' ? 'Listo legacy' : 'En empaque';

const PackagingManagement = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [busyId, setBusyId] = useState(null);

  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);
      setMessage({ type: '', text: '' });
      const res = await adminService.getPackagingQueue({ limit: 80 });
      const list = res?.orders ?? [];
      setOrders(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error(e);
      setMessage({ type: 'error', text: 'No se pudo cargar la cola de empaque.' });
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP' }).format(value || 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('es-CO', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const promoteToProcessing = async (orderId) => {
    if (!window.confirm('¿Marcar como empaquetado y pasar la orden a “procesando” (picking / guía)?')) {
      return;
    }
    try {
      setBusyId(orderId);
      setMessage({ type: '', text: '' });
      await adminService.updateOrderStatus(orderId, { status: 'processing' });
      setMessage({ type: 'success', text: 'Pedido pasado a procesando.' });
      await fetchQueue();
      window.dispatchEvent(new Event(ADMIN_OPS_REFRESH));
    } catch (e) {
      setMessage({
        type: 'error',
        text: e.response?.data?.message || e.message || 'Error al actualizar el pedido'
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="packaging-management">
      <div className="container">
        <div className="section-header">
          <h2>Empaque</h2>
          <p>
            Pedidos pagados que faltan por empaquetar. Cada línea muestra el tipo de empaque del producto
            (los cuatro tamaños configurados). Al terminar, pasa la orden al siguiente estado operativo.
          </p>
        </div>

        {message.text ? (
          <div className={`message-banner ${message.type}`} role="status">
            {message.text}
          </div>
        ) : null}

        <button type="button" className="btn-refresh-pkg" onClick={() => fetchQueue()} disabled={loading}>
          Actualizar lista
        </button>

        {loading ? (
          <div className="loading-container">
            <p>Cargando cola…</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="pkg-empty">
            No hay pedidos en cola de empaque (estado “en empaque”) en este momento.
          </div>
        ) : (
          orders.map((order) => {
            const lines = Array.isArray(order.packaging_lines) ? order.packaging_lines : [];
            return (
              <div key={order.id} className="order-pack-card">
                <div className="order-pack-head">
                  <div className="order-pack-meta">
                    <strong>
                      {(order.order_number || `#${order.id}`)} · {formatCurrency(order.total)}
                    </strong>
                    <span>
                      {order.customer_name || '—'} · {order.customer_email || '—'} ·{' '}
                      {formatDate(order.created_at)}
                    </span>
                  </div>
                  <span className={`badge-status ${statusBadgeClass(order.status)}`}>
                    {statusBadgeText(order.status)}
                  </span>
                </div>
                <table className="pkg-lines-table">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th>Cant.</th>
                      <th>Tipo de empaque</th>
                      <th>Despacho</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((row) => (
                      <tr key={row.id ?? `${row.product_id}-${row.product_name}`}>
                        <td>{row.product_name}</td>
                        <td>{row.quantity}</td>
                        <td className="pkg-type-cell">
                          <small>{packageLabel(row.package_type)}</small>
                        </td>
                        <td>{fulfillmentShort(row.fulfillment)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <button
                  type="button"
                  className="btn-pkg-done"
                  disabled={busyId === order.id}
                  onClick={() => promoteToProcessing(order.id)}
                >
                  {busyId === order.id ? 'Guardando…' : 'Marcar empaquetado → a procesar'}
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default PackagingManagement;
